/**
 * The substrates: list, create and delete.
 *
 * A substrate is more than a label: its length and width *are* the plate every
 * marking layout is bounded by, which is why they are required and must be
 * real sizes. Deletion is refused while an exploration still uses it.
 *
 * The page shares its look with the New exploration page: one card per
 * substrate (components/ResourceCard.js) under a group label, with the
 * summary panel and the actions beside them (components/ResourcePanel.js).
 */
import React, { useEffect, useState } from "react";
import {
  Stack,
  Button,
  Typography,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import LayersRoundedIcon from "@mui/icons-material/LayersRounded";
import axios from "axios";
import ResourcePageLayout from "../../components/ResourcePageLayout";
import ResourcePanel, { PanelButton } from "../../components/ResourcePanel";
import ResourceCard, { DeleteResourceButton, usagePill } from "../../components/ResourceCard";
import { LIST_SCOPE_ALL, withListScope } from "../../components/ListScopeToggle";
import { formatDateTime } from "../../helper/formatDate";

const emptyForm = {
  material_name: "",
  material_type: "",
  length: "",
  width: "",
  thickness: "",
};

/** One substrate: everything fits in the header, so the card does not expand. */
function SubstrateCard({ row, onDeleteRequest }) {
  const usedBy = row.explorations_count ?? 0;
  return (
    <ResourceCard
      cardKey={row.id}
      icon={<LayersRoundedIcon sx={{ fontSize: 20 }} />}
      title={row.material_name}
      caption={[row.created_by_email, row.created_at ? formatDateTime(row.created_at) : null]}
      pills={[
        { key: "material", label: "Material", value: row.material_type || "—" },
        {
          key: "plate",
          label: "Plate",
          value: `${row.length} × ${row.width} mm`,
          title: "Length × width; length along the laser x axis, width along y",
        },
        { key: "thickness", label: "Thickness", value: `${row.thickness} mm` },
        usagePill(usedBy),
      ]}
      actions={
        <DeleteResourceButton usedBy={usedBy} noun="substrate" onClick={() => onDeleteRequest(row)} />
      }
      expandable={false}
    />
  );
}

/**
 * Management page for substrates (materials): substrates are team-wide and
 * created here, not inside the exploration setup. They are IMMUTABLE — a
 * physical plate does not change, so there is no edit; a different plate is
 * a new substrate (same principle as camera calibrations). Deletion is
 * refused (server-side too) while any exploration references the substrate —
 * Exploration.material would cascade otherwise.
 */
export default function SubstratesOverview() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  // null = closed; {} = the create dialog (substrates are immutable — there
  // is no edit: a different plate is a new substrate, like calibrations).
  const [editorTarget, setEditorTarget] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [editorBusy, setEditorBusy] = useState(false);
  const [editorError, setEditorError] = useState(null);
  const [namingHelpOpen, setNamingHelpOpen] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  const loadSubstrates = async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const response = await axios.get(
        withListScope("api/color/explorer/material/", LIST_SCOPE_ALL)
      );
      setRows(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error("Loading substrates failed:", error);
      setLoadError("Could not load the substrates.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSubstrates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openCreate = () => {
    setForm(emptyForm);
    setEditorError(null);
    setEditorTarget({});
  };

  const setField = (key) => (event) =>
    setForm((prev) => ({ ...prev, [key]: event.target.value }));

  const numberValid = (value) => {
    const parsed = parseFloat(value);
    return Number.isFinite(parsed) && parsed > 0;
  };
  const formValid =
    form.material_name.trim().length > 0 &&
    form.material_type.trim().length > 0 &&
    numberValid(form.length) &&
    numberValid(form.width) &&
    numberValid(form.thickness);

  const flattenApiError = (error) => {
    const data = error?.response?.data;
    if (!data) return error?.message || "Request failed.";
    if (typeof data === "string") return data;
    if (data.error) return data.error;
    // DRF field-keyed validation errors, e.g. {"material_name": ["..."]}.
    return (
      Object.entries(data)
        .map(([key, value]) => `${key}: ${[].concat(value).join(" ")}`)
        .join("; ") || "Request failed."
    );
  };

  const handleEditorSave = async () => {
    if (!formValid || !editorTarget) return;
    setEditorBusy(true);
    setEditorError(null);
    const payload = {
      material_name: form.material_name.trim(),
      material_type: form.material_type.trim(),
      length: parseFloat(form.length),
      width: parseFloat(form.width),
      thickness: parseFloat(form.thickness),
    };
    try {
      await axios.post("api/color/explorer/material/", payload);
      setEditorTarget(null);
      await loadSubstrates();
    } catch (error) {
      console.error("Saving substrate failed:", error);
      setEditorError(flattenApiError(error));
    } finally {
      setEditorBusy(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleteBusy(true);
    setDeleteError(null);
    try {
      await axios.delete(
        `api/color/explorer/material/${encodeURIComponent(deleteTarget.id)}/`
      );
      setDeleteTarget(null);
      await loadSubstrates();
    } catch (error) {
      console.error("Deleting substrate failed:", error);
      setDeleteError(flattenApiError(error));
    } finally {
      setDeleteBusy(false);
    }
  };

  const inUse = rows.filter((row) => (row.explorations_count ?? 0) > 0).length;
  const panel = (
    <ResourcePanel
      overline="All substrates"
      headline={
        loading ? "Loading" : `${rows.length} ${rows.length === 1 ? "substrate" : "substrates"}`
      }
      subline={loading ? "" : `${inUse} in use · ${rows.length - inUse} unused`}
      about="A substrate is the physical plate: its length and width bound every marking layout. Substrates are never edited; a different plate is a new substrate, and a substrate in use cannot be deleted."
      actions={
        <>
          <PanelButton primary startIcon={<AddCircleOutlineIcon />} onClick={openCreate}>
            New substrate
          </PanelButton>
          <PanelButton startIcon={<RefreshIcon />} onClick={loadSubstrates} disabled={loading}>
            Refresh
          </PanelButton>
        </>
      }
    />
  );

  return (
    <>
      <ResourcePageLayout
        title="Substrates"
        loading={loading}
        loadingLabel="Loading substrates…"
        error={loadError}
        count={rows.length}
        emptyText={"No substrates yet. Create one with “New substrate”."}
        panel={panel}
      >
        {rows.map((row) => (
          <SubstrateCard
            key={row.id}
            row={row}
            onDeleteRequest={(target) => {
              setDeleteError(null);
              setDeleteTarget(target);
            }}
          />
        ))}
      </ResourcePageLayout>

      <Dialog
        open={Boolean(editorTarget)}
        onClose={() => !editorBusy && setEditorTarget(null)}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>New substrate</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              required
              label="Material"
              placeholder="e.g. SS, Ti"
              value={form.material_type}
              onChange={setField("material_type")}
              fullWidth
            />
            <Typography variant="subtitle2" color="text.secondary">
              Plate dimensions
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Length along the laser x axis, width along y.
            </Typography>
            <TextField
              required
              label="Length (mm)"
              placeholder="e.g. 130"
              value={form.length}
              onChange={setField("length")}
              type="number"
              fullWidth
            />
            <TextField
              required
              label="Width (mm)"
              placeholder="e.g. 130"
              value={form.width}
              onChange={setField("width")}
              type="number"
              fullWidth
            />
            <TextField
              required
              label="Thickness (mm)"
              placeholder="e.g. 1, 2"
              value={form.thickness}
              onChange={setField("thickness")}
              type="number"
              fullWidth
            />
            <TextField
              required
              label="Substrate Name"
              placeholder="e.g. SS-AISI304-U-F-1mm-P-RUPP"
              value={form.material_name}
              onChange={setField("material_name")}
              fullWidth
              helperText="Format: Material-Alloy-SurfaceFinish-Foiling-Thickness-Shape-Supplier"
            />
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ cursor: "pointer", textDecoration: "underline", alignSelf: "flex-start" }}
              onClick={() => setNamingHelpOpen(true)}
            >
              Click to see naming convention examples
            </Typography>
            {editorError ? (
              <Alert severity="error" variant="outlined" sx={{ borderRadius: 2 }}>
                {editorError}
              </Alert>
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditorTarget(null)} disabled={editorBusy}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleEditorSave}
            disabled={!formValid || editorBusy}
          >
            {editorBusy ? "Creating…" : "Create substrate"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={namingHelpOpen}
        onClose={() => setNamingHelpOpen(false)}
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Substrate Naming Convention</DialogTitle>
        <DialogContent>
          <Typography variant="h6" gutterBottom>
            Format:
          </Typography>
          <Typography variant="body1" gutterBottom>
            &lt;Material&gt;-&lt;Alloy&gt;-&lt;SurfaceFinish&gt;-&lt;Foiling&gt;-&lt;Thickness&gt;-&lt;Shape&gt;-&lt;Supplier&gt;
          </Typography>
          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
            Components:
          </Typography>
          <Typography variant="body1" gutterBottom>
            <strong>Material:</strong> SS = Stainless Steel, Ti = Titanium
          </Typography>
          <Typography variant="body1" gutterBottom>
            <strong>Alloy:</strong> e.g. AISI304
          </Typography>
          <Typography variant="body1" gutterBottom>
            <strong>Surface Finish:</strong> U = Untreated, P = Polished, S = Sanded
          </Typography>
          <Typography variant="body1" gutterBottom>
            <strong>Foiling:</strong> U = Unfoiled, F = 1-sided foiled, 2F = 2-sided foiled
          </Typography>
          <Typography variant="body1" gutterBottom>
            <strong>Thickness:</strong> e.g. 1mm, 2mm (include 'mm' in name)
          </Typography>
          <Typography variant="body1" gutterBottom>
            <strong>Shape:</strong> P = Plate, KN = Knife, CO = Coin, etc.
          </Typography>
          <Typography variant="body1" gutterBottom>
            <strong>Supplier:</strong> e.g. RUPP, WMF
          </Typography>
          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
            Examples:
          </Typography>
          <Typography variant="body1" gutterBottom>
            SS-AISI304-U-F-1mm-P-RUPP
          </Typography>
          <Typography variant="body1" gutterBottom>
            SS-AISI304-U-U-2mm-KN-WMF
          </Typography>
          <Typography variant="body1" gutterBottom>
            Ti-Grade2-P-2F-1.5mm-CO-RUPP
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNamingHelpOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(deleteTarget)}
        onClose={() => !deleteBusy && setDeleteTarget(null)}
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Delete substrate?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This permanently removes the substrate “{deleteTarget?.material_name}”. This cannot
            be undone.
          </DialogContentText>
          {deleteError ? (
            <Alert severity="error" variant="outlined" sx={{ mt: 2, borderRadius: 2 }}>
              {deleteError}
            </Alert>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleteBusy}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleConfirmDelete}
            disabled={deleteBusy}
          >
            {deleteBusy ? "Deleting…" : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
